/**
 * Shared in-flight HTTP request deduplication.
 *
 * Why this exists: the codebase has multiple HTTP libraries (apiUtils.js
 * apiRequest, enhancedApiService.js enhancedRequest, lineageApiService.js
 * direct axios). Each previously had its own dedup map (or none), so two
 * concurrent callers from different libraries hitting the same URL each
 * fired their own network request. Combined with React StrictMode double-
 * effects, parent re-mounts, and components that bypass React Query by
 * calling axios from a raw useEffect, this produced 5-10 duplicate
 * requests per dashboard mount. With Chrome's 6-connection-per-origin
 * HTTP/1.1 limit on localhost, the queue saturated and critical requests
 * timed out at 30s.
 *
 * This module gives every HTTP path a single shared dedup map keyed by
 * `METHOD:url:params`. Idempotent reads (GET, HEAD) are coalesced;
 * mutations (POST, PUT, DELETE, PATCH) are never coalesced because each
 * caller expects their write to land on the server.
 */

const inFlightRequests = new Map();

/**
 * Build a stable dedup key for an HTTP request, or return null to skip
 * dedup. Skips for non-idempotent methods.
 *
 * @param {string} method - HTTP method (case-insensitive)
 * @param {string} url - Request URL (may be relative or absolute)
 * @param {object} [params] - Query params object
 * @returns {string|null}
 */
export function buildDedupKey(method, url, params) {
  const normalized = (method || 'GET').toUpperCase();
  if (normalized !== 'GET' && normalized !== 'HEAD') return null;
  return `${normalized}:${url}:${JSON.stringify(params || {})}`;
}

/**
 * Run an HTTP request executor with cross-library in-flight deduplication.
 * If an identical request is already pending, callers receive the same
 * Promise instead of issuing a parallel network call.
 *
 * The dedup window ends when the underlying request settles (success or
 * failure), so a fresh call after that fires a new request — caching is
 * orthogonal and handled separately by each library.
 *
 * @template T
 * @param {string|null} dedupKey - From buildDedupKey()
 * @param {() => Promise<T>} executor - Performs the actual network call
 * @returns {Promise<T>}
 */
export function dedupedRequest(dedupKey, executor) {
  if (!dedupKey) return executor();

  const existing = inFlightRequests.get(dedupKey);
  if (existing) return existing;

  const promise = executor();
  inFlightRequests.set(dedupKey, promise);
  // Cleanup chain — `.finally(...)` returns a new promise that re-rejects
  // if the underlying promise rejected. Without the trailing `.catch(() => {})`,
  // node + jest log an "unhandled rejection" warning (the original promise's
  // rejection IS handled by the caller, but this auxiliary chain isn't).
  // The catch is intentionally a no-op; rejections still propagate to the
  // returned `promise`.
  promise.finally(() => {
    // Only clear if our promise is still the current one — defensive
    // against a hypothetical race where another request got registered
    // first (shouldn't happen with the synchronous .set above, but
    // costs nothing).
    if (inFlightRequests.get(dedupKey) === promise) {
      inFlightRequests.delete(dedupKey);
    }
  }).catch(() => {});
  return promise;
}
